import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterOutlined,
} from '@ant-design/icons';
import { Tag } from 'antd';

import {
  usePosItemSuggestions,
  usePosItemsSearch,
  type PosItem,
  type PosItemChild,
  type PosItemType,
} from '../api/usePosItemsSearch';

import { AdvancedItemSearchModal } from './AdvancedItemSearchModal';
import { ItemVariantSelectionModal } from './ItemVariantSelectionModal';
import { PosSelectableItemRow } from './PosSelectableItemRow';
import type { PosSaleLine } from '../types';
import { resolveLineKey } from '../utils/lineKeys';
import { SubscriptionBadge } from '@features/erp/subscriptions';
import {
  trackItemSelectedForContext,
  trackSearchEmptyResult,
  trackSearchPerformed,
  trackSearchResultSelected,
  type BehaviorAnalyticsContext,
} from '@tools/behaviorAnalytics.ts';

import './PosItemSearch.scss';

const SEARCH_INPUT_ID = 'pos-items-search-input';
const TRANSIENT_EXIT_DURATION_MS = 180;

type TransientSelectionSource = 'suggestion' | 'search';
type TransientSelectionPhase = 'visible' | 'exiting';

interface TransientSelection {
  key: string;
  source: TransientSelectionSource;
  sourceItem: PosItem;
  selectedItem: PosItem;
  position: number;
  searchSession: number;
  phase: TransientSelectionPhase;
  confirmedInCart: boolean;
}

interface SelectableItemEntry {
  key: string;
  sourceItem: PosItem;
  displayItem: PosItem;
  transientKey?: string;
  phase?: TransientSelectionPhase;
}

interface AdvancedFilters {
  categoryIds: number[];
  sku?: string;
  tags: string[];
}

const DEFAULT_FILTERS: AdvancedFilters = {
  categoryIds: [],
  sku: undefined,
  tags: [],
};

interface PosItemSearchProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  onSelectItem: (item: PosItem) => void;
  cartLines?: PosSaleLine[];
  onQuantityChange?: (lineKey: string, quantity: number) => void;
  onRemoveLine?: (lineKey: string) => void;
  incrementOnDuplicate?: boolean;
  customerId?: number | null;
  showTypeFilter?: boolean;
  behaviorContext?: BehaviorAnalyticsContext;
  defaultItemType?: PosItemType;
  selectionRetentionMs?: number;
}

export const PosItemSearch: React.FC<PosItemSearchProps> = ({
  inputRef,
  onSelectItem,
  cartLines = [],
  onQuantityChange,
  onRemoveLine,
  incrementOnDuplicate = true,
  customerId,
  showTypeFilter = true,
  behaviorContext,
  defaultItemType,
  selectionRetentionMs = 0,
}) => {
  const [query, setQuery] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState<PosItemType | undefined>(defaultItemType);
  const [filters, setFilters] = useState<AdvancedFilters>(DEFAULT_FILTERS);
  const [isAdvancedOpen, setAdvancedOpen] = useState(false);
  const [variantSelection, setVariantSelection] = useState<{
    item: PosItem;
    source: TransientSelectionSource;
    position: number;
    searchSession: number;
  } | null>(null);
  const [searchSession, setSearchSession] = useState(0);
  const searchSessionRef = useRef(0);
  const [dismissedSearchItemIds, setDismissedSearchItemIds] = useState<Set<number>>(() => new Set());
  const [transientSelections, setTransientSelections] = useState<Record<string, TransientSelection>>({});
  const transientSelectionsRef = useRef<Record<string, TransientSelection>>({});
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const exitTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const focusedTransientKeysRef = useRef<Set<string>>(new Set());

  const trimmedQuery = query.trim();
  const hasAdvancedFilters =
    filters.categoryIds.length > 0 || Boolean(filters.sku) || filters.tags.length > 0;
  const shouldSearch = trimmedQuery.length > 0 || hasAdvancedFilters;
  const itemSuggestionsEnabled = import.meta.env.VITE_SYSTEM_TRACKING_ITEM_SUGGESTIONS_ENABLED !== 'false';
  const shouldLoadSuggestions = itemSuggestionsEnabled && Boolean(behaviorContext) && !shouldSearch;
  const filtersCount =
    filters.categoryIds.length + (filters.sku ? 1 : 0) + filters.tags.length + (itemTypeFilter ? 1 : 0);
  const searchId = useMemo(
    () => [
      'pos_item_search',
      trimmedQuery || 'empty',
      itemTypeFilter ?? 'all',
      filters.categoryIds.join('-') || 'no_categories',
      filters.sku ?? 'no_sku',
      filters.tags.join('-') || 'no_tags',
    ].join(':'),
    [filters.categoryIds, filters.sku, filters.tags, itemTypeFilter, trimmedQuery],
  );
  const lastTrackedSearchRef = useRef<string | null>(null);
  const retentionEnabled = selectionRetentionMs > 0 && Boolean(onQuantityChange && onRemoveLine);

  const commitTransientSelections = useCallback(
    (updater: (current: Record<string, TransientSelection>) => Record<string, TransientSelection>) => {
      setTransientSelections((current) => {
        const next = updater(current);
        transientSelectionsRef.current = next;
        return next;
      });
    },
    [],
  );

  const clearTransientTimers = useCallback((key: string) => {
    const dismissTimer = dismissTimersRef.current.get(key);
    if (dismissTimer) clearTimeout(dismissTimer);
    dismissTimersRef.current.delete(key);

    const exitTimer = exitTimersRef.current.get(key);
    if (exitTimer) clearTimeout(exitTimer);
    exitTimersRef.current.delete(key);
  }, []);

  const removeTransientSelection = useCallback((key: string) => {
    clearTransientTimers(key);
    focusedTransientKeysRef.current.delete(key);
    commitTransientSelections((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, [clearTransientTimers, commitTransientSelections]);

  const finishTransientExit = useCallback((key: string) => {
    const selection = transientSelectionsRef.current[key];
    if (!selection) return;

    if (
      selection.source === 'search'
      && selection.searchSession === searchSessionRef.current
      && selection.sourceItem.id === selection.selectedItem.id
    ) {
      setDismissedSearchItemIds((current) => {
        const next = new Set(current);
        next.add(selection.sourceItem.id);
        return next;
      });
    }
    removeTransientSelection(key);
  }, [removeTransientSelection]);

  const beginTransientExit = useCallback((key: string) => {
    if (focusedTransientKeysRef.current.has(key) || !transientSelectionsRef.current[key]) return;

    commitTransientSelections((current) => {
      const selection = current[key];
      if (!selection) return current;
      return { ...current, [key]: { ...selection, phase: 'exiting' } };
    });

    const reduceMotion = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const exitTimer = setTimeout(
      () => finishTransientExit(key),
      reduceMotion ? 0 : TRANSIENT_EXIT_DURATION_MS,
    );
    exitTimersRef.current.set(key, exitTimer);
  }, [commitTransientSelections, finishTransientExit]);

  const restartTransientTimer = useCallback((key: string) => {
    clearTransientTimers(key);
    commitTransientSelections((current) => {
      const selection = current[key];
      if (!selection) return current;
      return { ...current, [key]: { ...selection, phase: 'visible' } };
    });
    if (focusedTransientKeysRef.current.has(key) || selectionRetentionMs <= 0) return;

    const timer = setTimeout(() => beginTransientExit(key), selectionRetentionMs);
    dismissTimersRef.current.set(key, timer);
  }, [beginTransientExit, clearTransientTimers, commitTransientSelections, selectionRetentionMs]);

  const retainSelection = useCallback((payload: Omit<TransientSelection, 'key' | 'phase' | 'confirmedInCart'>) => {
    if (!retentionEnabled) return undefined;
    const key = `${payload.source}:${payload.searchSession}:${payload.sourceItem.id}:${payload.selectedItem.id}`;
    clearTransientTimers(key);
    commitTransientSelections((current) => ({
      ...current,
      [key]: {
        ...payload,
        key,
        phase: 'visible',
        confirmedInCart: current[key]?.confirmedInCart ?? false,
      },
    }));
    const timer = setTimeout(() => beginTransientExit(key), selectionRetentionMs);
    dismissTimersRef.current.set(key, timer);
    return key;
  }, [beginTransientExit, clearTransientTimers, commitTransientSelections, retentionEnabled, selectionRetentionMs]);

  const handleTransientFocusChange = useCallback((key: string, focused: boolean) => {
    if (focused) {
      focusedTransientKeysRef.current.add(key);
      clearTransientTimers(key);
      return;
    }
    focusedTransientKeysRef.current.delete(key);
    restartTransientTimer(key);
  }, [clearTransientTimers, restartTransientTimer]);

  useEffect(() => () => {
    dismissTimersRef.current.forEach((timer) => clearTimeout(timer));
    exitTimersRef.current.forEach((timer) => clearTimeout(timer));
  }, []);

  const searchResult = usePosItemsSearch(
    {
      q: trimmedQuery.length > 0 ? trimmedQuery : undefined,
      itemType: itemTypeFilter,
      categoryIds: filters.categoryIds,
      sku: filters.sku,
      tags: filters.tags,
      includeSubscriptionPlans: true,
    },
    {
      enabled: shouldSearch,
    },
  );
  const suggestionsResult = usePosItemSuggestions(
    {
      context: behaviorContext ?? {
        appArea: 'erp',
        feature: 'pos',
        surface: 'fullscreen',
        componentId: 'item_search',
      },
      itemType: itemTypeFilter,
      limit: 10,
      excludeItemIds: cartLines.flatMap((line) => [line.item.id, line.item.parent_id]).filter((id): id is number => id != null),
    },
    {
      enabled: shouldLoadSuggestions,
    },
  );

  const { results, currency } = useMemo(() => {
    const currentResults = searchResult.data?.results ?? [];
    const firstCurrency = currentResults.find((item) => item.price?.currency)?.price?.currency;
    return {
      results: currentResults,
      currency: firstCurrency,
    };
  }, [searchResult.data?.results]);
  const excludedSuggestionIds = useMemo(
    () => new Set(cartLines.flatMap((line) => [line.item.id, line.item.parent_id]).filter((id): id is number => id != null)),
    [cartLines],
  );
  const suggestions = useMemo(
    () => (suggestionsResult.data?.items ?? []).filter((item) => !excludedSuggestionIds.has(item.id)).slice(0, 10),
    [excludedSuggestionIds, suggestionsResult.data?.items],
  );
  const visibleItems = useMemo(
    () => shouldSearch ? results.filter((item) => !dismissedSearchItemIds.has(item.id)) : [],
    [dismissedSearchItemIds, results, shouldSearch],
  );

  const transientSelectionList = useMemo(
    () => Object.values(transientSelections),
    [transientSelections],
  );

  const suggestionEntries = useMemo<SelectableItemEntry[]>(() => {
    const entries: SelectableItemEntry[] = suggestions.map((item) => ({
      key: `suggestion:${item.id}`,
      sourceItem: item,
      displayItem: item,
    }));
    const retained = transientSelectionList
      .filter((selection) => selection.source === 'suggestion')
      .sort((left, right) => left.position - right.position);

    retained.forEach((selection) => {
      const withoutSelection = entries.filter((entry) => (
        entry.sourceItem.id !== selection.sourceItem.id
        && entry.displayItem.id !== selection.selectedItem.id
      ));
      const insertAt = Math.max(0, Math.min(selection.position - 1, withoutSelection.length));
      withoutSelection.splice(insertAt, 0, {
        key: selection.key,
        sourceItem: selection.sourceItem,
        displayItem: selection.selectedItem,
        transientKey: selection.key,
        phase: selection.phase,
      });
      entries.splice(0, entries.length, ...withoutSelection);
    });

    return entries.slice(0, 10);
  }, [suggestions, transientSelectionList]);

  const searchEntries = useMemo<SelectableItemEntry[]>(() => {
    const retainedBySourceId = new Map(
      transientSelectionList
        .filter((selection) => selection.source === 'search' && selection.searchSession === searchSession)
        .map((selection) => [selection.sourceItem.id, selection]),
    );

    return visibleItems.map((item) => {
      const selection = retainedBySourceId.get(item.id);
      return selection ? {
        key: `search:${item.id}`,
        sourceItem: item,
        displayItem: selection.selectedItem,
        transientKey: selection.key,
        phase: selection.phase,
      } : {
        key: `search:${item.id}`,
        sourceItem: item,
        displayItem: item,
      };
    });
  }, [searchSession, transientSelectionList, visibleItems]);

  useEffect(() => {
    const current = transientSelectionsRef.current;
    const confirmedKeys: string[] = [];
    const removedKeys: string[] = [];

    Object.values(current).forEach((selection) => {
      const isInCart = cartLines.some((line) => line.item.id === selection.selectedItem.id && line.quantity > 0);
      if (isInCart && !selection.confirmedInCart) confirmedKeys.push(selection.key);
      if (!isInCart && selection.confirmedInCart) removedKeys.push(selection.key);
    });

    removedKeys.forEach(removeTransientSelection);
    if (confirmedKeys.length > 0) {
      commitTransientSelections((selections) => {
        const next = { ...selections };
        confirmedKeys.forEach((key) => {
          if (next[key]) next[key] = { ...next[key], confirmedInCart: true };
        });
        return next;
      });
    }
  }, [cartLines, commitTransientSelections, removeTransientSelection]);

  const resetSearchRetention = useCallback(() => {
    searchSessionRef.current += 1;
    setSearchSession(searchSessionRef.current);
    setDismissedSearchItemIds(new Set());
    Object.values(transientSelectionsRef.current)
      .filter((selection) => selection.source === 'search')
      .forEach((selection) => removeTransientSelection(selection.key));
  }, [removeTransientSelection]);

  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    resetSearchRetention();
    setQuery(event.target.value);
  }, [resetSearchRetention]);

  const handleItemTypeFilterChange = useCallback((itemType: PosItemType | undefined) => {
    resetSearchRetention();
    setItemTypeFilter(itemType);
  }, [resetSearchRetention]);

  const triggerSelect = useCallback(
    (item: PosItem, position = 1) => {
      const selectionSource: TransientSelectionSource = shouldSearch ? 'search' : 'suggestion';
      // 1.d: Ítems grupales no se pueden agregar directamente
      if (item.service_kind === 'group') {
        return;
      }

      // Los padres siempre abren el selector; sólo los hijos llegan al carrito.
      if (item.is_parent) {
        if ((item.children?.length ?? 0) === 0) {
          return;
        }
        setVariantSelection({
          item,
          source: selectionSource,
          position,
          searchSession: searchSessionRef.current,
        });
        return;
      }

      if (behaviorContext) {
        const itemId = String(item.id);
        const source = shouldSearch ? 'manual_search' : 'suggestion';
        trackSearchResultSelected(behaviorContext, {
          search_id: searchId,
          result_type: 'item',
          result_id: itemId,
          position,
          query_length: trimmedQuery.length,
          source,
        });
        trackItemSelectedForContext(behaviorContext, {
          item_id: itemId,
          item_type: item.item_type ?? null,
          search_id: searchId,
          position,
          source,
        });
      }

      retainSelection({
        source: selectionSource,
        sourceItem: item,
        selectedItem: item,
        position,
        searchSession: searchSessionRef.current,
      });
      onSelectItem(item);
      if (!retentionEnabled && incrementOnDuplicate) {
        setQuery('');
      }
      if (inputRef.current) {
        inputRef.current.focus();
        if (retentionEnabled && shouldSearch) inputRef.current.select();
      }
    },
    [behaviorContext, incrementOnDuplicate, inputRef, onSelectItem, retainSelection, retentionEnabled, searchId, shouldSearch, trimmedQuery],
  );

  const handleVariantSelected = useCallback(
    (parentItem: PosItem, child: PosItemChild) => {
      const itemToAdd: PosItem = {
        ...child,
        is_parent: false,
        children: [],
      };

      if (behaviorContext) {
        const itemId = String(child.id);
        trackSearchResultSelected(behaviorContext, {
          search_id: searchId,
          result_type: 'item',
          result_id: itemId,
          position: variantSelection?.position ?? 1,
          query_length: trimmedQuery.length,
          source: 'variant_selector',
        });
        trackItemSelectedForContext(behaviorContext, {
          item_id: itemId,
          item_type: child.item_type ?? null,
          search_id: searchId,
          position: variantSelection?.position ?? 1,
          source: 'variant_selector',
        });
      }

      if (variantSelection) {
        retainSelection({
          source: variantSelection.source,
          sourceItem: parentItem,
          selectedItem: itemToAdd,
          position: variantSelection.position,
          searchSession: variantSelection.searchSession,
        });
      }
      onSelectItem(itemToAdd);
      setVariantSelection(null);
      if (!retentionEnabled && incrementOnDuplicate) {
        setQuery('');
      }
      if (inputRef.current) {
        inputRef.current.focus();
        if (retentionEnabled && variantSelection?.source === 'search') inputRef.current.select();
      }
    },
    [behaviorContext, incrementOnDuplicate, inputRef, onSelectItem, retainSelection, retentionEnabled, searchId, trimmedQuery.length, variantSelection],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const first = visibleItems[0];
        if (first) {
          event.preventDefault();
          event.stopPropagation();
          triggerSelect(first, 1);
        }
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setAdvancedOpen(true);
      }
    },
    [triggerSelect, visibleItems],
  );

  const clearFilters = useCallback(() => {
    resetSearchRetention();
    setFilters(DEFAULT_FILTERS);
    setItemTypeFilter(undefined);
  }, [resetSearchRetention]);

  const handleOpenAdvanced = useCallback(() => {
    setAdvancedOpen(true);
  }, []);

  const handleCloseAdvanced = useCallback(() => {
    setAdvancedOpen(false);
  }, []);

  const handleApplyAdvanced = useCallback(
    (payload: { query: string; filters: AdvancedFilters }) => {
      resetSearchRetention();
      setQuery(payload.query);
      setFilters(payload.filters);
      setAdvancedOpen(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [inputRef, resetSearchRetention],
  );

  const showEmptyState = shouldSearch && !searchResult.isFetching && results.length === 0;

  useEffect(() => {
    if (!behaviorContext || !shouldSearch || searchResult.isFetching) {
      return;
    }
    const status = searchResult.isError ? 'error' : results.length === 0 ? 'empty' : 'ok';
    const trackingKey = `${searchId}:${status}:${results.length}`;
    if (lastTrackedSearchRef.current === trackingKey) {
      return;
    }
    lastTrackedSearchRef.current = trackingKey;
    const payload = {
      search_id: searchId,
      query_length: trimmedQuery.length,
      result_count: results.length,
      filters_count: filtersCount,
      has_query: trimmedQuery.length > 0,
      status,
    };
    trackSearchPerformed(behaviorContext, payload);
    if (!searchResult.isError && results.length === 0) {
      trackSearchEmptyResult(behaviorContext, payload);
    }
  }, [
    behaviorContext,
    filtersCount,
    results.length,
    searchId,
    searchResult.isError,
    searchResult.isFetching,
    shouldSearch,
    trimmedQuery.length,
  ]);

  const activeFilterLabels = useMemo(() => {
    const labels: string[] = [];
    if (itemTypeFilter) {
      labels.push(`Tipo: ${itemTypeFilter === 'product' ? 'Productos' : 'Servicios'}`);
    }
    if (filters.categoryIds.length > 0) {
      labels.push(`Categorías (${filters.categoryIds.length})`);
    }
    if (filters.sku) {
      labels.push(`SKU: ${filters.sku}`);
    }
    if (filters.tags.length > 0) {
      labels.push(`Tags (${filters.tags.join(', ')})`);
    }
    return labels;
  }, [filters.categoryIds.length, filters.sku, filters.tags, itemTypeFilter]);

  return (
    <section aria-label="Buscador de ítems" className="pos-item-search">
      <header className="pos-item-search__header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label htmlFor={SEARCH_INPUT_ID} style={{ fontWeight: 600, fontSize: '0.9rem' }}>
            Buscar ítem (F2)
          </label>
          <span
            aria-hidden
            style={{
              fontSize: '0.75rem',
              color: 'var(--ant-color-text-secondary, #595959)',
              border: '1px solid var(--ant-color-border, #d9d9d9)',
              borderRadius: '0.5rem',
              padding: '0.1rem 0.35rem',
            }}
          >
            ⌘K
          </span>
        </div>

        <div className="pos-item-search__input-row">
          <span aria-hidden style={{ color: 'var(--ant-color-text-secondary, #8c8c8c)' }}>
            🔍
          </span>
          <input
            id={SEARCH_INPUT_ID}
            ref={inputRef}
            type="search"
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            placeholder="Nombre, SKU o código de barras"
            aria-label="Buscar ítem"
            autoComplete="off"
            className="pos-item-search__input"
          />
          <button
            type="button"
            onClick={handleOpenAdvanced}
            className={`pos-item-search__advanced-btn${filtersCount > 0 ? ' has-active' : ''}`}
            title="Búsqueda avanzada con filtros (⌘K)"
          >
            <FilterOutlined />
            <span>Filtros</span>
            {filtersCount > 0 ? <span>({filtersCount})</span> : null}
          </button>
        </div>

        {/* 1.a: Filtro por tipo estilo Segmented Control Chips */}
        {showTypeFilter ? (
          <div className="pos-item-search__type-chips" role="radiogroup" aria-label="Filtrar por tipo">
            <button
              type="button"
              className={`pos-item-search__type-chip${itemTypeFilter === undefined ? ' pos-item-search__type-chip--active' : ''}`}
              onClick={() => handleItemTypeFilterChange(undefined)}
            >
              Todos
            </button>
            <button
              type="button"
              className={`pos-item-search__type-chip${itemTypeFilter === 'product' ? ' pos-item-search__type-chip--active' : ''}`}
              onClick={() => handleItemTypeFilterChange('product')}
            >
              Productos
            </button>
            <button
              type="button"
              className={`pos-item-search__type-chip${itemTypeFilter === 'service' ? ' pos-item-search__type-chip--active' : ''}`}
              onClick={() => handleItemTypeFilterChange('service')}
            >
              Servicios
            </button>
          </div>
        ) : null}
      </header>

      {/* Chips de Filtros Activos */}
      {activeFilterLabels.length > 0 ? (
        <div aria-live="polite" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          {activeFilterLabels.map((label) => (
            <Tag key={label} color="blue" closable onClose={clearFilters}>
              {label}
            </Tag>
          ))}
          <button
            type="button"
            onClick={clearFilters}
            style={{
              background: 'none',
              border: 'none',
              color: '#595959',
              cursor: 'pointer',
              fontSize: '0.75rem',
              textDecoration: 'underline',
            }}
          >
            Limpiar filtros
          </button>
        </div>
      ) : null}

      {/* Ítems frecuentes y resultados comparten la misma fila seleccionable. */}
      {!shouldSearch && suggestionEntries.length > 0 ? (
        <section className="pos-item-search__frequent-section" aria-label="Ítems frecuentes">
          <div className="pos-item-search__frequent-title">
            <span>⚡ Más vendidos / Frecuentes</span>
          </div>
          <div className="pos-item-search__item-list">
            {suggestionEntries.map((entry, index) => {
              const cartLine = cartLines.find((line) => line.item.id === entry.displayItem.id && line.quantity > 0) ?? null;
              return (
                <PosSelectableItemRow
                  key={entry.key}
                  item={entry.displayItem}
                  currency={currency}
                  cartLine={cartLine}
                  onAdd={() => triggerSelect(entry.sourceItem, index + 1)}
                  onOpenVariants={() => triggerSelect(entry.sourceItem, index + 1)}
                  onQuantityChange={cartLine && onQuantityChange ? (quantity) => {
                    if (entry.transientKey) restartTransientTimer(entry.transientKey);
                    onQuantityChange(resolveLineKey(cartLine), quantity);
                  } : undefined}
                  onRemove={cartLine && onRemoveLine ? () => {
                    if (entry.transientKey) removeTransientSelection(entry.transientKey);
                    onRemoveLine(resolveLineKey(cartLine));
                  } : undefined}
                  isExiting={entry.phase === 'exiting'}
                  onFocusWithinChange={entry.transientKey
                    ? (focused) => handleTransientFocusChange(entry.transientKey!, focused)
                    : undefined}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Estados de Carga / Error */}
      {searchResult.isError ? (
        <div role="alert" style={{ color: '#a8071a', fontSize: '0.85rem' }}>
          No se pudo realizar la búsqueda. Intente nuevamente.
        </div>
      ) : null}

      {searchResult.isFetching ? <div role="status" style={{ fontSize: '0.85rem' }}>Buscando ítems…</div> : null}

      {showEmptyState ? (
        <p style={{ fontSize: '0.85rem', color: '#8c8c8c' }}>
          No se encontraron ítems que coincidan con la búsqueda.
        </p>
      ) : null}

      {/* Resultados de Búsqueda Activa */}
      {searchEntries.length > 0 ? (
        <ul role="listbox" aria-label="Resultados de ítems" className="pos-item-search__item-list pos-item-search__item-list--results">
          {searchEntries.map((entry, index) => {
            const cartLine = cartLines.find((line) => line.item.id === entry.displayItem.id && line.quantity > 0) ?? null;

            return (
              <li key={entry.key}>
                <PosSelectableItemRow
                  item={entry.displayItem}
                  currency={currency}
                  cartLine={cartLine}
                  accessory={customerId && !entry.displayItem.is_parent ? (
                    <SubscriptionBadge customerId={customerId} itemId={entry.displayItem.id} queryEnabled={shouldSearch} />
                  ) : null}
                  onAdd={() => triggerSelect(entry.sourceItem, index + 1)}
                  onOpenVariants={() => triggerSelect(entry.sourceItem, index + 1)}
                  onQuantityChange={cartLine && onQuantityChange ? (quantity) => {
                    if (entry.transientKey) restartTransientTimer(entry.transientKey);
                    onQuantityChange(resolveLineKey(cartLine), quantity);
                  } : undefined}
                  onRemove={cartLine && onRemoveLine ? () => {
                    if (entry.transientKey) removeTransientSelection(entry.transientKey);
                    onRemoveLine(resolveLineKey(cartLine));
                  } : undefined}
                  isExiting={entry.phase === 'exiting'}
                  onFocusWithinChange={entry.transientKey
                    ? (focused) => handleTransientFocusChange(entry.transientKey!, focused)
                    : undefined}
                />
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Modal Búsqueda Avanzada */}
      <AdvancedItemSearchModal
        isOpen={isAdvancedOpen}
        onClose={handleCloseAdvanced}
        defaultQuery={query}
        defaultFilters={filters}
        onApply={handleApplyAdvanced}
      />

      {/* Modal Variantes de Ítems Padre */}
      <ItemVariantSelectionModal
        open={variantSelection != null}
        item={variantSelection?.item ?? null}
        onClose={() => setVariantSelection(null)}
        onSelectChild={handleVariantSelected}
      />
    </section>
  );
};

export default PosItemSearch;
